/**
 * Certification Type Enum
 * Defines the types of certifications that can be uploaded
 */
export type CertificationType =
  | 'faa-part-107'      // User - Remote Pilot Certificate
  | 'faa-part-108'      // User - Future replacement for Part 107
  | 'faa-part-137'      // Organization - Agricultural Aircraft Operations
  | 'faa-44807'         // Organization - Exemption for drone operations
  | 'state-applicator'  // User or Organization - State pesticide applicator license
  | 'medical'           // User - Medical certificate
  | 'insurance'         // Organization - Liability insurance
  | 'other';            // Other certifications

/**
 * Certification ownership type
 */
export type CertificationOwnerType = 'organization' | 'profile';

/**
 * Certification status
 */
export type CertificationStatus = 'active' | 'expired' | 'expiring-soon' | 'pending-review' | 'archived';

/**
 * Certification interface
 */
export interface Certification {
  id: string;
  certificationType: CertificationType;
  ownerType: CertificationOwnerType;
  organizationId: string | null;
  profileId: string | null;
  certificateNumber: string;
  issuedDate: string;
  expirationDate: string | null;
  issuingAuthority: string;
  state: string | null;
  documentUrl: string | null;
  documentFileName: string | null;
  status: CertificationStatus;
  previousCertificationId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  deletedBy: string | null;
}

/**
 * Interface for creating certifications
 */
export interface CertificationCreateRequest {
  certificationType: CertificationType;
  ownerType: CertificationOwnerType;
  organizationId?: string;
  profileId?: string;
  certificateNumber: string;
  issuedDate: string;
  expirationDate?: string;
  issuingAuthority: string;
  state?: string;
  documentUrl?: string;
  documentFileName?: string;
  notes?: string;
  previousCertificationId?: string;
}

/**
 * Interface for updating certifications
 */
export interface CertificationUpdateRequest {
  certificateNumber?: string;
  issuedDate?: string;
  expirationDate?: string;
  issuingAuthority?: string;
  state?: string;
  documentUrl?: string;
  documentFileName?: string;
  notes?: string;
  status?: CertificationStatus;
}

/**
 * Certification type display names
 */
export const CERTIFICATION_TYPE_NAMES: Record<CertificationType, string> = {
  'faa-part-107': 'FAA Part 107',
  'faa-part-108': 'FAA Part 108',
  'faa-part-137': 'FAA Part 137',
  'faa-44807': 'FAA 44807',
  'state-applicator': 'State Applicator License',
  'medical': 'Medical Certificate',
  'insurance': 'Insurance',
  'other': 'Other'
};

/**
 * Helper function to get certification status badge class
 */
export function getCertificationStatusClass(status: CertificationStatus | null | undefined, expirationDate: string | null | undefined): string {
  if (!status) return 'bg-secondary';

  // Calculate status from expiration date if provided
  if (expirationDate) {
    const expDate = new Date(expirationDate);
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    if (expDate < now) {
      return 'bg-danger';
    }

    if (expDate < thirtyDaysFromNow) {
      return 'bg-warning';
    }

    return 'bg-success';
  }

  // Otherwise use provided status
  switch (status) {
    case 'active':
      return 'bg-success';
    case 'expiring-soon':
      return 'bg-warning';
    case 'expired':
      return 'bg-danger';
    case 'pending-review':
      return 'bg-info';
    case 'archived':
      return 'bg-secondary';
    default:
      return 'bg-secondary';
  }
}

/**
 * Helper function to get certification status display text
 */
export function getCertificationStatusText(status: CertificationStatus | null | undefined, expirationDate: string | null | undefined): string {
  if (!status) return 'Unknown';

  // Calculate status from expiration date if provided
  if (expirationDate) {
    const expDate = new Date(expirationDate);
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    if (expDate < now) {
      return 'Expired';
    }

    if (expDate < thirtyDaysFromNow) {
      return 'Expiring Soon';
    }

    return 'Valid';
  }

  // Otherwise use provided status
  switch (status) {
    case 'active':
      return 'Valid';
    case 'expiring-soon':
      return 'Expiring Soon';
    case 'expired':
      return 'Expired';
    case 'pending-review':
      return 'Pending Review';
    case 'archived':
      return 'Archived';
    default:
      return 'Unknown';
  }
}

/**
 * Helper function to get status icon
 */
export function getCertificationStatusIcon(status: CertificationStatus | null | undefined, expirationDate: string | null | undefined): string {
  if (!status) return '';

  // Calculate status from expiration date if provided
  if (expirationDate) {
    const expDate = new Date(expirationDate);
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    if (expDate < now) {
      return '❌';
    }

    if (expDate < thirtyDaysFromNow) {
      return '🟡';
    }

    return '🟢';
  }

  // Otherwise use provided status
  switch (status) {
    case 'active':
      return '🟢';
    case 'expiring-soon':
      return '🟡';
    case 'expired':
      return '❌';
    case 'pending-review':
      return '⏳';
    case 'archived':
      return '📦';
    default:
      return '';
  }
}
